import { useEffect, useRef, useState } from 'react';

/**
 * Intraday leg notionals, buffered in the browser — the live tail of the pair
 * chart, after the candle history.
 *
 * Samples are throttled and the buffer is capped, because the WS price feed can
 * tick several times a second and an unbounded array behind a chart is how a
 * trading page starts leaking on a long session.
 */

export interface PnlSample {
    /** Unix seconds — the unit lightweight-charts wants. */
    time: number;
    /** Futures notional for one contract, raw VND. */
    futValue: number | null;
    /** Spread basket value (fixed at the previous close), raw VND. */
    equityValue: number | null;
}

export interface PnlHistoryInput {
    futValue: number | null;
    equityValue: number | null;
}

/** One sample every 5s is plenty for a chart a human reads. */
const SAMPLE_INTERVAL_MS = 5000;
/** ~4h of 5s samples. Past this the oldest points fall off the left. */
const MAX_SAMPLES = 2880;

export function usePnlHistory(input: PnlHistoryInput, enabled = true): PnlSample[] {
    const [samples, setSamples] = useState<PnlSample[]>([]);
    // The effect must not re-run on every tick, so the newest values ride in a
    // ref and a single interval does the sampling.
    const latest = useRef(input);
    latest.current = input;

    useEffect(() => {
        if (!enabled) return;

        const take = () => {
            const v = latest.current;
            if (v.futValue == null && v.equityValue == null) return;
            setSamples((prev) => {
                const time = Math.floor(Date.now() / 1000);
                // lightweight-charts refuses a series whose times are not strictly
                // ascending, and two samples can land in the same second.
                if (prev.length && prev[prev.length - 1].time >= time) return prev;
                const next = [...prev, { ...v, time }];
                return next.length > MAX_SAMPLES ? next.slice(next.length - MAX_SAMPLES) : next;
            });
        };

        take(); // one point immediately, so the chart is never blank on open
        const id = window.setInterval(take, SAMPLE_INTERVAL_MS);
        return () => window.clearInterval(id);
    }, [enabled]);

    return samples;
}

/** Buckets the chart offers. Anything ≥ 1 day needs a stored series. */
export type PnlInterval = '5s' | '1min' | '5min' | '1h';

export const PNL_INTERVAL_SECONDS: Record<PnlInterval, number> = {
    '5s': 0, // 0 ⇒ bucketSamples is a no-op, i.e. the raw sample rate
    '1min': 60,
    '5min': 300,
    '1h': 3600,
};

/**
 * Down-sample the 5s buffer onto an interval, keeping the LAST sample of each
 * bucket.
 *
 * Last, not average: these series are a level (the mark right now), not a flow.
 * Averaging a mark invents a value the market never had, and both legs must come
 * from the same sample or the spread between them is fiction.
 */
export function bucketSamples(samples: PnlSample[], intervalSec: number): PnlSample[] {
    if (intervalSec <= 0 || samples.length === 0) return samples;
    const out: PnlSample[] = [];
    let bucket = -1;
    for (const s of samples) {
        const b = Math.floor(s.time / intervalSec);
        if (b === bucket) out[out.length - 1] = s; // later sample in the same bucket wins
        else {
            out.push(s);
            bucket = b;
        }
    }
    return out;
}
